import { ExhibitionCollectionFragment } from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { HeadlineText, BodyText, CalendarPlusIcon } from 'design-configuration/components'
import { ExhibitionImageCurl } from '../../[slug]/components/exhibition-image-curl'
import { WorksDigitalReleaseCard } from '../../[slug]/components/works-digital-card'
import { WorksPhysicalReleaseCard } from '../../[slug]/components/works-physical-card'
import { larsAnchor, larsShopifyProduct, larsTitle } from '../display'
import { WorksQuote } from '../../[slug]/components/works'
import { articleRoute } from '@utils/routes'
import { AddToCalendarButton, getProjectEvent, TrackedAddToCalButtonNames } from '@components/add-to-calendar'
import { useProjectStatus } from '@/hooks/use-project-status'
import { ValidStatusType } from '@utils/project'
import { getPhysicalProjectEvent } from '../../[slug]/utils/exhibition-utils'
import { GROUP_EXHIBITION_Q4_2024_SLUG } from '@consts/common'

export const LarsWork = ({ collection }: { collection?: ExhibitionCollectionFragment }): React.ReactElement | null => {
  const digitalProject = collection?.Collection?.Project ?? undefined
  const physicalProject = collection?.AdditionalProjects?.[0]?.Project ?? undefined

  const { status: digitalProjectStatus } = useProjectStatus(digitalProject)
  const { status: physicalProjectStatus } = useProjectStatus(physicalProject)

  if (!digitalProject || !physicalProject) {
    return null
  }

  const digitalEvent = getProjectEvent(digitalProject)
  const physicalEvent = getPhysicalProjectEvent(physicalProject, GROUP_EXHIBITION_Q4_2024_SLUG)

  return (
    <div className="gap-y-2xl bg-surface-low flex flex-col py-[80px] md:gap-y-[80px]">
      <ExhibitionImageCurl
        anchor={larsAnchor}
        align={'left'}
        image1AspectRatio="aspect-[4/5]"
        image1={getPrefixedLinkPath('/images/poetics/larsCollection/larsCollection-3.jpg')}
        image2={getPrefixedLinkPath('/images/poetics/larsCollection/larsCollection-2.jpg')}
        image3={getPrefixedLinkPath('/images/poetics/larsCollection/larsCollection-1.jpg')}
        content={
          <>
            <div className="flex items-center justify-between">
              <div>
                <HeadlineText className="font-medium">{larsTitle}</HeadlineText>
                <HeadlineText className="font-light">Lars Wander</HeadlineText>
              </div>
              {digitalProjectStatus === ValidStatusType.upcoming ||
              physicalProjectStatus === ValidStatusType.upcoming ? (
                <AddToCalendarButton
                  events={[physicalEvent, digitalEvent]}
                  buttonName={TrackedAddToCalButtonNames.exhibitionWorksDescription}
                  colorTheme={'outline'}
                  className="bg-transparent"
                >
                  <div className="gap-x-xs flex items-center">
                    <CalendarPlusIcon className="fill-on-surface h-sm w-sm" />
                    <BodyText>Add to calendar</BodyText>
                  </div>
                </AddToCalendarButton>
              ) : null}
            </div>
            <BodyText>
              Lars Wander navigates infinity through a latticework of mathematical forms in{' '}
              <span className="italic">while true (fabric)</span>, a single edition of four jacquard panels that
              captures structured rhythms in a tangible medium. This exclusive jacquard weaving presents a
              self-sustaining world, inviting viewers to explore the intersection of infinity and finite structures.
              Building on this concept, the digital release of <span className="italic">while true</span> invites
              viewers to delve deeper, where Wander challenges notions of completion, opening pathways that extend
              beyond the visible. His investigation into endless, cycling configurations highlights the interplay
              between space and boundary, drawing viewers into an ever-expanding exploration of form.
            </BodyText>
          </>
        }
      />
      <div className="gap-md px-md lg:gap-2xl lg:px-2xl flex w-full flex-col justify-center md:flex-row">
        <WorksPhysicalReleaseCard
          project={physicalProject}
          exhibitionSlug={GROUP_EXHIBITION_Q4_2024_SLUG}
          title={'while true (fabric)'}
          image={getPrefixedLinkPath('/images/poetics/larsCollection/larsCollection-4.jpg')}
          moreDetailsBlocks={[
            {
              title: 'Edition details',
              description: (
                <BodyText>
                  Crafted from an artist-selected hash code, these jacquard fabric panels reflect the rich variety of
                  <span className="italic">while true</span>. The fabric is paired with a digital artwork selected by
                  the artist.
                </BodyText>
              )
            },
            {
              title: 'Product specifications',
              description: (
                <BodyText>
                  Each jacquard fabric panel measures 126 inches in length by 57 inches in width, yielding a total of 14
                  yards across four panels. Produced from a sustainable, durable nylon and polyester blend (19% nylon
                  and 81% recycled polyester), this fabric is fully opaque and ideal for installation as drapery, or may
                  be converted for functional upholstery.
                </BodyText>
              )
            },
            {
              title: 'Sales notes',
              description: (
                <BodyText>
                  Available for purchase with credit card, Shop Pay, Apple Pay, Google Pay, PayPal, or Ethereum.
                  Purchase includes four jacquard fabric panels and corresponding digital artwork, which can be claimed
                  upon purchase.
                </BodyText>
              )
            },
            {
              title: 'Shipping & returns',
              description: (
                <BodyText>
                  Please allow one week for shipping after purchase. Shipping is only available in North America (U.S.,
                  Canada, & Mexico) and the European Union and the U.K. Shipping and handling is complimentary with all
                  orders, however duties and taxes may still apply at your destination. No returns.
                </BodyText>
              )
            }
          ]}
          description={
            <div className="my-sm">
              <BodyText className="font-medium">
                Single edition of 4 jacquard fabric panels with digital artwork
              </BodyText>
              <BodyText>Fixed price of $3,000 USD</BodyText>
            </div>
          }
          shopifyProduct={larsShopifyProduct}
        />
        <WorksDigitalReleaseCard
          project={digitalProject}
          title={'while true'}
          moreDetailsBlocks={[
            {
              title: 'Release details',
              description: (
                <div className="gap-y-xs flex flex-col">
                  <BodyText>
                    In a ranked auction with settlement, bids are placed by collectors and ranked from highest to
                    lowest. Any outbid funds are returned immediately. At the conclusion of the auction, winners pay the
                    lowest winning bid price, and any excess funds are returned upon minting the artwork.
                  </BodyText>
                  <BodyText className="text-on-surface-variant">
                    Time extensions apply: the auction will extend by up to 5 minutes if a bid is placed in the final 5
                    minutes, with a maximum total extension of 60 minutes.
                  </BodyText>
                  <AllPurposeLink
                    href={articleRoute('art-blocks-how-to-purchase')}
                    className="text-on-surface-variant gap-x-2xs flex items-center underline"
                  >
                    <BodyText>Learn more</BodyText>
                  </AllPurposeLink>
                </div>
              )
            },
            {
              title: 'Sales Notes',
              description: (
                <BodyText>
                  All bids must be placed in ETH. Winners will receive their digital artwork directly in their wallets
                  after the auction.
                </BodyText>
              )
            }
          ]}
          description={
            <div className="my-sm">
              <BodyText className="font-medium">60 unique digital artworks</BodyText>
              <BodyText>Ranked auction with settlement with a starting bid of 0.05 Ξ</BodyText>
            </div>
          }
        />
      </div>
      <WorksQuote
        quote={
          <>
            {`“I'm drawn to concepts and ideas that feel like they are at the edge of what's explainable. Oftentimes these
            ideas don't truly take hold until I've both accepted and rejected them, leaving me in a state of disbelief,
            unsure of what is true or false. This feeling can be challenging, but also deeply rewarding if I take the
            time to engage and push through the confusion. Art has a special place in fostering wonder and curiosity: it
            owes no answers; it can only guide toward asking the right questions.”`}
          </>
        }
        author="Lars Wander"
        image={getPrefixedLinkPath('/images/poetics/larsCollection/larsCollection-5.jpg')}
        alignText="right"
      />
    </div>
  )
}
